<template>
  <SidePanel
    label="copy details"
    :show="isCopyingDetails"
    :reset-listener="resetListener"
    @close="onClickCloseCopyDetails"
  >
    <h2><VisibleText>Copy Details</VisibleText></h2>
    <p>
      <VisibleText>Choose questions you want to copy details to.</VisibleText>
    </p>

    <div style="margin: 1rem 0" v-if="Object.keys(clientQuestion).length > 0">
      <VisibleText class="text-smallcaps text-smallcaps--title"> Your Question </VisibleText>
      <p>{{ clientQuestion.question_title }} - {{ clientQuestion.question_text }} <br /></p>
    </div>

    <VisibleText class="text-smallcaps text-smallcaps--title"> Select Questions </VisibleText>

    <SearchBar
      class="dataset__search-questions"
      placeholder="Search question code and question text"
      @search="questionSearch = $event"
    >
      <template #label> Search </template>
      <template #submit>
        <ButtonIconOnly icon="search" button-type="submit"> search </ButtonIconOnly>
      </template>
    </SearchBar>

    <form class="dataset-table__copy-details-form dataset__select-questions-form" @submit.prevent>
      <fieldset class="form-fieldset-group light-scroll-bar" id="copy-details-questions-group">
        <legend class="sr-only">
          {{ translate('select question', $options.name) }}
        </legend>
        <div
          class="dataset__select-questions-form__checkbox"
          v-for="question in filteredQuestions"
          :key="`select-${question._id.$oid}`"
        >
          <span class="colspan-2">
            <label class="form-checkbox-label" :for="`gq-${question._id.$oid}`">
              <input
                class="form-checkbox-input"
                type="checkbox"
                :disabled="
                  question.approved_data_type !== clientQuestion.approved_data_type ||
                  question._id.$oid === clientQuestionId
                "
                :id="`gq-${question._id.$oid}`"
                :name="question._id.$oid"
                :value="question._id.$oid"
                v-model="selectedQuestions"
              />
              <span class="question-title">
                {{ question.question_title }}
              </span>
              <span class="question-text">
                {{ question.question_text }}
              </span>
            </label>
          </span>
          <CopyDetailsPannelValuesList class="colspan-2" :question="question" />
        </div>
      </fieldset>
      <button
        type="submit"
        class="side-pannel__primary-btn"
        :disabled="!this.permissions.includes('update-dataset')"
        @click="onClickCopyDetails"
      >
        <VisibleText>Copy Details</VisibleText>
      </button>
      <button type="button" class="side-pannel__close-btn" @click="onClickCloseCopyDetails">
        <VisibleText>Close</VisibleText>
      </button>
      <MessageInline :message="message.text" :message-type="message.type" />
    </form>
  </SidePanel>
</template>

<script>
// Components
import CopyDetailsPannelValuesList from './CopyDetailsPannelValuesList.vue';
import MessageInline from '@/components/UI/Message/MessageInline.vue';
import SidePanel from '@/components/UI/SidePanel.vue';
import ButtonIconOnly from '@/components/UI/Button/ButtonIconOnly.vue';
import SearchBar from '@/components/UI/SearchBar.vue';

import FiltersMixin from '@/utils/mixins/filtersMixin.js';
import UserMixin from '@/utils/mixins/userMixin.js';

export default {
  name: 'CopyDetailsPannel',
  components: {
    ButtonIconOnly,
    CopyDetailsPannelValuesList,
    MessageInline,
    SearchBar,
    SidePanel,
  },
  mixins: [FiltersMixin, UserMixin],
  props: {
    clientQuestionId: {
      default: () => '',
      type: String,
      required: true,
    },
  },
  data() {
    return {
      message: {
        type: '',
        text: '',
      },
      valueToLabel: new Map(),
      questionSearch: null,
      selectedQuestions: [],
      resetListener: false,
    };
  },
  computed: {
    clientQuestion() {
      return (
        this.$store.getters['datasetDetails/getClientQuestions'].find(
          (question) => question._id.$oid === this.clientQuestionId,
        ) || {}
      );
    },
    filteredQuestions() {
      const keys = ['question_title', 'question_text'];
      return this.searchInObjectValues(
        this.$store.getters['datasetDetails/getClientQuestions'],
        keys,
        this.questionSearch,
      );
    },
    isCopyingDetails() {
      return this.$store.getters['datasetWizard/getCopyingDetails'].isCopyingDetails;
    },
    visiblePlaceholder() {
      return this.checkTranslationCache(
        'search questions',
        'search__questions',
        this.$store.getters.getLanguage,
      );
    },
  },
  methods: {
    onClickCloseCopyDetails() {
      this.resetMessage();
      this.$store.dispatch('datasetWizard/setCopyingDetails', {
        isCopyingDetails: false,
        methodName: 'copyValAndLabel', //default value
      });
    },
    async onClickCopyDetails() {
      this.resetMessage();
      await this[this.$store.getters['datasetWizard/getCopyingDetails'].methodName]();

      this.selectedQuestions = [];
    },
    resetMessage() {
      this.message = {
        text: '',
        type: '',
      };
    },
    async copyValAndLabel() {
      this.$store.dispatch('datasetWizard/setSpinner', {
        complete: 'data types successfully saved',
        isLoading: true,
      });

      // update all selected questions
      await Promise.all(
        this.selectedQuestions.map(async (questionId) => {
          const clientQuestion = this.$store.getters['datasetDetails/getClientQuestions'].find(
            (question) => question._id.$oid === questionId,
          );
          const uniqueValues = clientQuestion.unique_values.reduce((uniquevals, value) => {
            if (this.valueToLabel.has(value.value)) {
              uniquevals.push(this.valueToLabel.get(value.value));
            } else uniquevals.push(value);
            return uniquevals;
          }, []);

          // patch & save to store
          const updatedQ = await this.$services.DATASETS_SERVICE.updateClientQuestion(
            clientQuestion._id.$oid,
            { unique_values: uniqueValues },
          );
          this.$store.dispatch(
            'datasetDetails/updateClientQuestion',
            updatedQ.client_question_object,
          );
        }),
      )
        .then(() => {
          this.message = {
            text: 'Values and labels copied.',
            type: 'success',
          };
          this.$store.dispatch('datasetWizard/setSpinner', {
            complete: 'data types successfully saved',
            isLoading: false,
          });
        })
        .catch(() => {
          this.message = {
            text: 'There was a problem saving the details. Please try again.',
            type: 'error',
          };
        });
    },
  },
  watch: {
    clientQuestionId: {
      immediate: true,
      handler(id) {
        if (id && Object.keys(this.clientQuestion).length > 0) {
          this.clientQuestion.unique_values.forEach((value) => {
            this.valueToLabel.set(value.value, value);
          });
          this.resetListener = !this.resetListener;
        }
      },
    },
  },
};
</script>
